// ============================================================
// notify.tsx — Notifications non bloquantes (toasts) + confirm async
//
// Remplace window.alert() / window.confirm() qui, sous Electron, gèlent la
// saisie clavier : après fermeture de la boîte native, le focus n'est pas rendu
// au contenu web tant qu'on n'a pas re-focalisé la fenêtre.
//
// Usage :
//   import { notify, confirmDialog } from "../utils/notify";  (ajuster le chemin)
//   notify("✅ Enregistré");                 // toast succès (défaut)
//   notify("❌ Erreur ...", "error");        // toast erreur
//   if (!(await confirmDialog("Supprimer ?"))) return;   // dans une fn async
//
// <ToastHost /> est monté une seule fois à la racine de l'app (voir index.tsx).
// ============================================================
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

type ToastType = "success" | "error" | "info";

interface ToastItem { id: number; message: string; type: ToastType; }
interface ConfirmReq { id: number; message: string; resolve: (v: boolean) => void; }

let toasts: ToastItem[] = [];
let confirms: ConfirmReq[] = [];
let seq = 1;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Devine le type d'un message quand il n'est pas fourni (remplacement direct des alert()). */
function detectType(message: string): ToastType {
  const m = String(message);
  if (/❌|⛔|🚫|[Ee]rreur|[ÉéEe]chec|invalide|impossible|introuvable|manquant|requis|obligatoire|refus|interdit/.test(m)) {
    return "error";
  }
  if (/✅|🎉|👍|enregistr|succ[èe]s|ajout|supprim|modifi|cr[ée]{2}|mis à jour|effectu|valid/i.test(m)) {
    return "success";
  }
  return "info";
}

/** Toast non bloquant. Remplace alert(). Si type absent, il est déduit du message. */
export function notify(message: string, type?: ToastType): void {
  const id = seq++;
  toasts = [...toasts, { id, message, type: type ?? detectType(message) }];
  emit();
  window.setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    emit();
  }, 3200);
}

/** Confirmation non bloquante. Remplace window.confirm(). À utiliser avec await. */
export function confirmDialog(message: string): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const id = seq++;
    confirms = [...confirms, { id, message, resolve }];
    emit();
  });
}

function resolveConfirm(id: number, value: boolean) {
  const req = confirms.find((c) => c.id === id);
  confirms = confirms.filter((c) => c.id !== id);
  emit();
  if (req) req.resolve(value);
}

const TOAST_COLORS: Record<ToastType, string> = {
  success: "#27ae60",
  error: "#e74c3c",
  info: "#2980b9",
};

/** Monté une seule fois à la racine. Rend les toasts + les modales de confirmation. */
export const ToastHost: React.FC = () => {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((x) => x + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);

  return createPortal(
    <>
      {/* Toasts */}
      <div
        style={{
          position: "fixed", top: 16, right: 16, zIndex: 100000,
          display: "flex", flexDirection: "column", gap: 8, pointerEvents: "none",
        }}
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            style={{
              background: TOAST_COLORS[t.type], color: "white",
              padding: "12px 18px", borderRadius: 10,
              boxShadow: "0 6px 24px rgba(0,0,0,0.25)", fontWeight: 700,
              maxWidth: 380, whiteSpace: "pre-line",
            }}
          >
            {t.message}
          </div>
        ))}
      </div>

      {/* Modales de confirmation */}
      {confirms.map((c) => (
        <div
          key={c.id}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100001,
          }}
        >
          <div style={{ background: "white", borderRadius: 14, padding: 28, maxWidth: 460, width: "90%", boxShadow: "0 12px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 15, color: "#2c3e50", marginBottom: 22, whiteSpace: "pre-line", lineHeight: 1.5 }}>{c.message}</div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button
                onClick={() => resolveConfirm(c.id, false)}
                style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: "#ecf0f1", color: "#555", fontWeight: 700, cursor: "pointer" }}
              >
                Annuler
              </button>
              <button
                onClick={() => resolveConfirm(c.id, true)}
                style={{ padding: "10px 18px", borderRadius: 8, border: "none", background: "#e74c3c", color: "white", fontWeight: 700, cursor: "pointer" }}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      ))}
    </>,
    document.body
  );
};
